import { Card, Typography, Descriptions, Button } from "antd";
import PropTypes from "prop-types";
import "./ProfileSettings.css";
import { useState, useRef } from "react";
import Title from "antd/es/typography/Title";
import Link from "antd/es/typography/Link";
import { useNavigate } from "react-router-dom";

import { useSessionStore } from "../../../../store/session-store";
import { useAxiosPrivate } from "../../../../hooks/useAxiosPrivate";
import { useAlertStore } from "../../../../store/alert-store";
import { useExceptionHandler } from "../../../../hooks/useExceptionHandler";
import { ConfirmModal } from "../../../../components/widgets/confirm-modal/ConfirmModal";
import { LLM_WHISPERER_ENDPOINT } from "../../helper";

const pricingData = [
  { key: "1", label: "Native Text", children: "$1/1000 pages" },
  { key: "2", label: "Low Cost", children: "$5/1000 pages" },
  { key: "3", label: "High Quality", children: "$10/1000 pages" },
  {
    key: "4",
    label: "High Quality with Form Elements",
    children: "$15/1000 pages",
  },
];

const SubscriptionCard = ({
  name,
  product,
  isSubscribed,
  cancelSubscription,
  isFreePlan,
  stripePromise,
  proPlanProduct,
}) => {
  const { Text } = Typography;
  const { sessionDetails } = useSessionStore();
  const axiosPrivate = useAxiosPrivate();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const isCancelled = useRef(false);

  const handleClick = (event) => {
    setIsLoading(true);
    isCancelled.current = false;

    // Cancel the process if user navigates back (popstate)
    const handlePopState = () => {
      isCancelled.current = true;
      setIsLoading(false);
    };
    window.addEventListener("popstate", handlePopState);

    // Set a 30-second timeout to cancel the process if stripe redirect isn't triggered.
    const timer = setTimeout(() => {
      if (!isCancelled.current) {
        isCancelled.current = true;
        setAlertDetails(handleException("Stripe redirect timed out"));
        setIsLoading(false);
      }
    }, 30000);

    stripePromise
      .then((stripe) => {
        if (isCancelled.current) {
          return Promise.reject(new Error("Cancelled"));
        }
        const requestOptions = {
          method: "POST",
          url: `${LLM_WHISPERER_ENDPOINT}/create-checkout-session/`,
          headers: {
            "X-CSRFToken": sessionDetails?.csrfToken,
          },
          data: {
            product_id: proPlanProduct,
          },
        };

        return axiosPrivate(requestOptions).then((response) => {
          if (isCancelled.current) {
            return Promise.reject(new Error("Cancelled"));
          }
          return { stripe, sessionId: response?.data?.session_id };
        });
      })
      .then(({ stripe, sessionId }) => {
        if (isCancelled.current) {
          return Promise.reject(new Error("Cancelled"));
        }
        return stripe.redirectToCheckout({ sessionId }).then((result) => {
          if (isCancelled.current) {
            return;
          }
          if (result.error) {
            setAlertDetails(
              handleException(result.error, "Subscription not found")
            );
          }
        });
      })
      .catch((err) => {
        if (err.message !== "Cancelled") {
          setAlertDetails(handleException(err, "Failed to subscribe"));
        }
      })
      .finally(() => {
        setIsLoading(false);
        clearTimeout(timer);
        window.removeEventListener("popstate", handlePopState);
      });
  };

  return (
    <>
      <h2 className="plan-status-title">
        {isFreePlan
          ? "Your Current Plan"
          : isSubscribed
          ? "Currently subscribed to the LLMWhisperer pro plan"
          : "Simple, no-commitment, pay-as-you-go-pricing"}
      </h2>
      <Card
        className={`pricing-card ${
          isFreePlan ? "free-plan-card" : "pro-plan-card"
        }`}
      >
        <Title level={3} className="plan-title">
          {isFreePlan ? "LLMWhisperer Free Plan" : "LLMWhisperer Pro Plan"}
        </Title>
        <Text strong>Processing Modes Pricing:</Text>
        <div className="plan-card-content-container">
          <Descriptions
            className="pricing-table"
            bordered
            size="small"
            column={1}
            items={pricingData}
          />
          <div className="plan-card-right-container">
            {!isFreePlan && isSubscribed ? (
              <div className="link-container">
                <Link
                  onClick={() =>
                    navigate(
                      `/llm-whisperer/${sessionDetails.orgName}/dashboard`
                    )
                  }
                >
                  See usage details
                </Link>
                <Link
                  href="https://billing.stripe.com/p/login/cN2bKwcBz9jrcDe4gg"
                  target="_blank"
                >
                  Past Invoices
                </Link>
                <ConfirmModal
                  handleConfirm={() => cancelSubscription(proPlanProduct)}
                  content={`Want to Unsubscribe ${name}`}
                  cancelText={"Go back"}
                  okText={"Confirm Cancel"}
                >
                  <Link>Unsubscribe</Link>
                </ConfirmModal>
              </div>
            ) : (
              <>
                <Title level={3} className="plan-title">
                  Limited by the free plan?
                </Title>
                <Title level={5} className="plan-title">
                  Subscribe to
                </Title>
                <Button
                  type="primary"
                  className="pro-subscribe-button"
                  onClick={handleClick}
                  loading={isLoading}
                >
                  LLMWhisperer Pro Plan
                </Button>
                <Text italic>
                  {isFreePlan
                    ? "See below for details on Processing Modes"
                    : "Go beyond the limits of the free plan"}
                </Text>
              </>
            )}
          </div>
        </div>
        {isFreePlan ? (
          <Text strong>
            Maximum of 100 calls / day <br />
            across all processing modes
          </Text>
        ) : (
          <Text italic className="footer-text">
            See below for details on Processing Modes
          </Text>
        )}
      </Card>
    </>
  );
};

SubscriptionCard.propTypes = {
  name: PropTypes.string,
  quota: PropTypes.string,
  monthlyQuota: PropTypes.string,
  product: PropTypes.string,
  isSubscribed: PropTypes.bool,
  interval: PropTypes.string,
  cancelSubscription: PropTypes.any,
  isFreePlan: PropTypes.bool,
  stripePromise: PropTypes.any,
  description: PropTypes.string,
  proPlanProduct: PropTypes.string,
};

export { SubscriptionCard };
